import { useRef, useState } from "react";
import { Loader2, ScanLine, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["pts", "reb", "ast", "stl", "blk", "fls"];

export default function ScanSheetDialog({ game, onClose, onApplied }) {
    const inputRef = useRef(null);
    const [side, setSide] = useState("home");
    const [file, setFile] = useState(null);
    const [reading, setReading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [rows, setRows] = useState(null);
    const [unmatched, setUnmatched] = useState([]);

    const teamName = side === "home" ? game.home_team?.name : game.away_team?.name;

    const read = async () => {
        if (!file) {
            toast.error("Choose a sheet photo first");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setReading(true);
        setRows(null);
        try {
            const { data } = await api.post(`/games/${game.id}/scoresheet/scan?side=${side}`, fd);
            setRows(data.rows);
            setUnmatched(data.unmatched || []);
            if (!data.rows.length) toast.error("No stats could be read — try a clearer, well-lit photo");
            else toast.success(`Read ${data.rows.length} player rows — verify before applying`);
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setReading(false);
        }
    };

    const apply = async () => {
        setApplying(true);
        try {
            const payload = {
                side,
                rows: rows.map((r) => ({
                    player_id: r.player_id,
                    ...Object.fromEntries(CATS.map((c) => [c, Math.max(0, Number(r[c]) || 0)])),
                })),
            };
            const { data } = await api.post(`/games/${game.id}/scoresheet/apply`, payload);
            toast.success(`Stats recorded for ${data.players} players`);
            onApplied?.();
            onClose();
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setApplying(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-[#12121A] border-[#262638] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display font-black uppercase text-2xl">Scan Stat Sheet</DialogTitle>
                    <DialogDescription className="text-slate-400 text-sm">
                        Photograph the filled sheet flat with all four corner markers visible. AI reads the numbers — always
                        verify before applying.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-wrap items-end gap-3 mt-2">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Team Side</Label>
                        <Select value={side} onValueChange={setSide}>
                            <SelectTrigger data-testid="scan-side-select" className="mt-2 w-48 bg-[#08080C] border-[#262638] text-white">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#12121A] border-[#262638] text-white">
                                <SelectItem value="home">Home — {game.home_team?.name}</SelectItem>
                                <SelectItem value="away">Away — {game.away_team?.name}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Sheet Photo</Label>
                        <button
                            type="button"
                            data-testid="scan-file-button"
                            onClick={() => inputRef.current?.click()}
                            className="mt-2 flex items-center gap-2 border border-[#262638] hover:border-[#FF5500] hover:text-[#FF5500] text-slate-300 font-display font-bold uppercase tracking-widest text-xs px-4 py-2.5 rounded-full transition-colors duration-200"
                        >
                            <Upload className="w-4 h-4" />
                            {file ? file.name.slice(0, 24) : "Choose photo"}
                        </button>
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            capture="environment"
                            className="hidden"
                            data-testid="scan-file-input"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                        />
                    </div>
                    <button
                        type="button"
                        data-testid="scan-read-button"
                        onClick={read}
                        disabled={reading || !file}
                        className="flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-50 text-white font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                    >
                        {reading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
                        {reading ? "Reading…" : "Read Sheet"}
                    </button>
                </div>

                {rows && (
                    <div className="mt-5">
                        <p className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">
                            Parsed — {teamName} ({rows.length} players)
                        </p>
                        {unmatched.length > 0 && (
                            <p data-testid="scan-unmatched" className="text-xs text-[#FFB800] mb-2">
                                Numbers not on this roster (skipped): {unmatched.map((u) => `#${u.number}`).join(", ")}
                            </p>
                        )}
                        <div className="rounded-xl border border-[#262638] overflow-hidden">
                            <table className="w-full text-sm" data-testid="scan-preview-table">
                                <thead>
                                    <tr className="bg-[#08080C] text-slate-400 text-[10px] uppercase tracking-widest">
                                        <th className="px-3 py-2 text-left">#</th>
                                        <th className="px-3 py-2 text-left">Player</th>
                                        {CATS.map((c) => (
                                            <th key={c} className="px-2 py-2 text-center">{c}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={r.player_id} data-testid={`scan-row-${r.player_id}`} className="border-t border-[#262638]">
                                            <td className="px-3 py-1.5 font-stat">{r.number}</td>
                                            <td className="px-3 py-1.5">{r.name}</td>
                                            {CATS.map((c) => (
                                                <td key={c} className="px-1 py-1.5">
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        data-testid={`scan-cell-${r.player_id}-${c}`}
                                                        value={r[c]}
                                                        onChange={(e) =>
                                                            setRows(rows.map((x, j) => (j === i ? { ...x, [c]: e.target.value } : x)))
                                                        }
                                                        className="w-14 bg-[#08080C] border border-[#262638] rounded-md px-2 py-1 text-center font-stat text-white focus:border-[#FF5500] focus:outline-none"
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <button
                            type="button"
                            data-testid="scan-apply-button"
                            onClick={apply}
                            disabled={applying || !rows.length}
                            className="mt-4 flex items-center gap-2 bg-[#22C55E] hover:bg-[#16A34A] disabled:opacity-50 text-black font-display font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-full transition-colors duration-200"
                        >
                            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
                            {applying ? "Applying…" : "Apply Stats to Game"}
                        </button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
